import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import Button from "../../../../../shared/components/Button";
import SecondaryIcon from "../../../../../shared/components/SecondaryIcon";
import Icons from "../../../../../shared/constants/icon.constant";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from "@/shared/components/ui/field";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  useUserBalances,
  useCreateFiatWithdrawal,
  useFiatWithdrawalFee,
} from "../../../services";
import type { FiatWithdrawalReceiverType } from "../../../interfaces/fiat.interface";
import { isApiError } from "@/shared/api";

interface WithdrawalFundsFiatProps {
  onSubmit(): void;
}

export default function WithdrawalFundsFiat({
  onSubmit,
}: Readonly<WithdrawalFundsFiatProps>) {
  const queryClient = useQueryClient();
  const {
    data: balancesData,
    isLoading: isLoadingBalances,
    error: balancesError,
  } = useUserBalances();

  const fiatBalances = useMemo(
    () => balancesData?.fiatBalances ?? [],
    [balancesData?.fiatBalances]
  );

  const currencyOptions = useMemo(
    () =>
      fiatBalances.map((balance) => {
        const symbol = (balance.name ?? "").toUpperCase();
        const displaySymbol = symbol || balance.id;

        return {
          label: displaySymbol,
          value: balance.id,
        };
      }),
    [fiatBalances]
  );

  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string>("");
  const [amount, setAmount] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [destinationIban, setDestinationIban] = useState("");
  const [transferTitle, setTransferTitle] = useState("");

  useEffect(() => {
    if (!currencyOptions.length) {
      setSelectedCurrencyId("");
      return;
    }

    if (!selectedCurrencyId) {
      setSelectedCurrencyId(currencyOptions[0]?.value ?? "");
      return;
    }

    const hasSelectedOption = currencyOptions.some(
      (option) => option.value === selectedCurrencyId
    );

    if (!hasSelectedOption) {
      setSelectedCurrencyId(currencyOptions[0]?.value ?? "");
    }
  }, [currencyOptions, selectedCurrencyId]);

  const selectedBalance = useMemo(
    () =>
      fiatBalances.find((balance) => balance.id === selectedCurrencyId) ?? null,
    [fiatBalances, selectedCurrencyId]
  );

  const selectedCurrencyCode = (selectedBalance?.name ?? "").toUpperCase();

  const currencyPlaceholder = useMemo(() => {
    if (isLoadingBalances) {
      return "Loading currencies...";
    }

    if (currencyOptions.length) {
      return "Select Currency";
    }

    return "No fiat balances found";
  }, [currencyOptions, isLoadingBalances]);

  const availableAmountLabel = useMemo(() => {
    if (!selectedBalance) {
      return null;
    }

    const symbol = selectedCurrencyCode || "Fiat";
    return `Available balance: ${selectedBalance.amount ?? "0"} ${symbol}`;
  }, [selectedBalance, selectedCurrencyCode]);

  const createFiatWithdrawal = useCreateFiatWithdrawal();
  const isSubmitting = createFiatWithdrawal.isPending;

  const amountNumber = Number.parseFloat(amount);
  const isValidAmount =
    Number.isFinite(amountNumber) &&
    amountNumber > 0 &&
    amount.trim().length > 0;

  const normalizedCurrencyCode = selectedCurrencyCode
    ? selectedCurrencyCode.toLowerCase()
    : "";
  const safeAmountNumber = Number.isFinite(amountNumber) ? amountNumber : 0;

  const feeQueryParams = useMemo(
    () => ({
      amount: safeAmountNumber,
      currency: normalizedCurrencyCode,
    }),
    [safeAmountNumber, normalizedCurrencyCode]
  );

  const shouldFetchFee =
    feeQueryParams.amount > 0 && feeQueryParams.currency.length > 0;

  const {
    data: withdrawalFee,
    isLoading: isLoadingFee,
    isError: isFeeError,
    refetch: refetchFee,
    isFetching: isFetchingFee,
  } = useFiatWithdrawalFee(feeQueryParams, {
    enabled: shouldFetchFee,
    retry: false,
  });

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const trimmedIban = destinationIban.trim();
  const trimmedTitle = transferTitle.trim();

  const receiverType: FiatWithdrawalReceiverType = "NATURAL";

  const isSubmitDisabled =
    isLoadingBalances ||
    !!balancesError ||
    !selectedCurrencyCode ||
    !trimmedFirstName ||
    !trimmedLastName ||
    !trimmedIban ||
    !trimmedTitle ||
    !isValidAmount ||
    isSubmitting ||
    (shouldFetchFee && (isLoadingFee || isFetchingFee));

  const feeInfo = useMemo(() => {
    if (!selectedCurrencyCode) {
      return {
        message: "Select a currency to see the withdrawal fee.",
        description: "",
        canRetry: false,
      };
    }

    if (!shouldFetchFee) {
      return {
        message: "Enter an amount to calculate the withdrawal fee.",
        description: "",
        canRetry: false,
      };
    }

    if (isLoadingFee || isFetchingFee) {
      return {
        message: "Calculating withdrawal fee…",
        description: "",
        canRetry: false,
      };
    }

    if (isFeeError) {
      return {
        message: "Unable to calculate the withdrawal fee right now.",
        description: "",
        canRetry: true,
      };
    }

    if (!withdrawalFee) {
      return {
        message: "Fee information is unavailable.",
        description: "",
        canRetry: false,
      };
    }

    const feeCurrency = (withdrawalFee.currency ?? normalizedCurrencyCode)
      .toUpperCase()
      .trim();
    const rawFee = withdrawalFee.fee;
    const numericFee =
      typeof rawFee === "number"
        ? rawFee
        : Number.parseFloat(String(rawFee ?? ""));
    const feeDisplay = Number.isFinite(numericFee)
      ? `${numericFee} ${feeCurrency}`
      : String(rawFee ?? "—");

    return {
      message: `Estimated fee: ${feeDisplay}`,
      description: withdrawalFee.description?.trim() ?? "",
      canRetry: false,
    };
  }, [
    selectedCurrencyCode,
    shouldFetchFee,
    isLoadingFee,
    isFetchingFee,
    isFeeError,
    withdrawalFee,
    normalizedCurrencyCode,
  ]);

  const handleWithdraw = () => {
    if (isSubmitting) {
      return;
    }

    if (balancesError) {
      toast.error(
        "Unable to submit withdrawal while balances are unavailable."
      );
      return;
    }

    if (!selectedCurrencyCode) {
      toast.error("Select a fiat currency to withdraw.");
      return;
    }

    if (!isValidAmount) {
      toast.error("Enter a valid withdrawal amount.");
      return;
    }

    if (!trimmedFirstName || !trimmedLastName) {
      toast.error("Receiver name is required.");
      return;
    }

    if (!trimmedIban) {
      toast.error("Destination IBAN is required.");
      return;
    }

    if (!trimmedTitle) {
      toast.error("Transfer title is required.");
      return;
    }

    const payload = {
      amount: amountNumber,
      currency: normalizedCurrencyCode,
      receiverType,
      destinationIban: trimmedIban,
      transferTitle: trimmedTitle,
      receiverFirstName: trimmedFirstName,
      receiverLastName: trimmedLastName,
    };

    createFiatWithdrawal.mutate(payload, {
      onSuccess: () => {
        toast.success("Fiat withdrawal request submitted successfully.");
        setAmount("");
        setFirstName("");
        setLastName("");
        setDestinationIban("");
        setTransferTitle("");
        void queryClient.invalidateQueries({
          queryKey: ["fiatWithdrawalHistory"],
        });
        void queryClient.invalidateQueries({ queryKey: ["user-balances"] });
        onSubmit();
      },
      onError: (error: unknown) => {
        let message = "Failed to submit withdrawal request.";
        let requestId: string | undefined;

        if (isApiError(error)) {
          message = error.message;
          requestId = error.requestId;
        } else if (error instanceof Error) {
          message = error.message;
        }

        toast.error(message, {
          description: requestId ? `Request ID: ${requestId}` : undefined,
        });
      },
    });
  };

  return (
    <div className="space-y-6">
      <FieldSet>
        <FieldGroup>
          <Field>
            <FieldLabel>Fiat Currency</FieldLabel>
            <FieldContent>
              <Select
                value={selectedCurrencyId || undefined}
                onValueChange={setSelectedCurrencyId}
                disabled={isLoadingBalances || !currencyOptions.length}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={currencyPlaceholder} />
                </SelectTrigger>
                <SelectContent align="start" className="min-w-44">
                  {currencyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldDescription>
                Choose the fiat balance you want to withdraw from.
              </FieldDescription>
              {availableAmountLabel ? (
                <p className="text-xs text-muted-foreground mt-1">
                  {availableAmountLabel}
                </p>
              ) : null}
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>Receiver First Name</FieldLabel>
            <FieldContent>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g. John"
              />
              <FieldDescription>
                Enter the first name of the account holder who will receive the
                funds.
              </FieldDescription>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>Receiver Last Name</FieldLabel>
            <FieldContent>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="e.g. Doe"
              />
              <FieldDescription>
                This must match the beneficiary name registered with your bank.
              </FieldDescription>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>Destination IBAN</FieldLabel>
            <FieldContent>
              <Input
                value={destinationIban}
                onChange={(e) => setDestinationIban(e.target.value)}
                placeholder="Enter account number or IBAN"
              />
              <FieldDescription>
                Provide the destination account where the funds should be
                transferred.
              </FieldDescription>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>Transfer Title</FieldLabel>
            <FieldContent>
              <Input
                value={transferTitle}
                onChange={(e) => setTransferTitle(e.target.value)}
                placeholder="Reference for this transfer"
              />
              <FieldDescription>
                Add a reference or note that will appear on the bank transfer.
              </FieldDescription>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>Amount</FieldLabel>
            <FieldContent>
              <div className="relative">
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="pr-14"
                />
                <span className="text-muted-foreground absolute inset-y-0 right-3 flex items-center text-xs">
                  {selectedCurrencyCode || "FIAT"}
                </span>
              </div>
              <FieldDescription>
                Enter the withdrawal amount you would like to transfer.
              </FieldDescription>
              {feeInfo.message ? (
                <p className="text-xs text-muted-foreground mt-2">
                  {feeInfo.message}
                </p>
              ) : null}
              {feeInfo.description ? (
                <p className="text-xs text-muted-foreground">
                  {feeInfo.description}
                </p>
              ) : null}
              {feeInfo.canRetry ? (
                <button
                  type="button"
                  className="text-xs text-white underline"
                  onClick={() => refetchFee()}>
                  Retry fee calculation
                </button>
              ) : null}
            </FieldContent>
          </Field>
        </FieldGroup>
      </FieldSet>

      <div>
        <Button
          onClick={handleWithdraw}
          className="w-full"
          disabled={isSubmitDisabled}>
          Withdraw
        </Button>
        {balancesError ? (
          <p className="text-xs text-red-400 mt-2">
            Unable to load fiat balances right now. Please try again later.
          </p>
        ) : null}
      </div>

      <div className="text-xs text-label-dark space-y-4 pt-4">
        <p>
          By Clicking the Withdraw button, I agree to the Terms of the{" "}
          <b className="text-white">Customer Agreement</b> and the{" "}
          <b className="text-white">Anti-Money Laundering Policy</b>, and
          confirm that the{" "}
          <b className="text-white">
            payment will not be made from a third-party account
          </b>{" "}
          .
        </p>
        {/* <div className="border-t border-gray-700 border-dashed"></div> */}
        {/* <div className="flex items-center gap-2 mb-2">
          <SecondaryIcon
            icon={Icons.DepositAmountIcon({ width: 15, height: 15 })}
            isActive
          />
          <p className="text-white">Minimum Amount: $3</p>
        </div> */}
        <div className="border-t border-gray-700 border-dashed"></div>
        <div className="flex items-center gap-2 mb-2">
          <SecondaryIcon
            icon={Icons.TimeIcon({ width: 15, height: 15 })}
            isActive
          />
          <p className="text-white">
            Processing time: Up to 2 hours (may vary)
          </p>
        </div>
        <div className="border-t border-gray-700 border-dashed"></div>
        <div className="flex items-center gap-2 mb-2">
          <SecondaryIcon
            icon={Icons.InfoIcon({ width: 15, height: 15 })}
            isActive
          />
          <a href="#" className="text-white underline">
            Additional info.
          </a>
        </div>
      </div>
    </div>
  );
}
